import { Button, Flex, Modal, OptimizeImage, Stack, Text, Textarea } from '@inflearn/ds-react-next';
import { useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { API_ENDPOINT } from '../../../../../resources/@types/accountSchema';
import { useGamesResultsResultIdUpdateMutation } from '../../../../mutations/useGamesResultsResultIdUpdateMutation';

interface SlotDressUpResultModalProps {
  isOpen: boolean;
  imageUrl: string | null;
  resultId: number | null;
  onClose: () => void;
  onCommentSubmit: () => void;
}

export const SlotDressUpResultModal = ({
  isOpen,
  imageUrl,
  resultId,
  onClose,
  onCommentSubmit,
}: SlotDressUpResultModalProps) => {
  const [comment, setComment] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const queryClient = useQueryClient();

  const { mutate: updateComment } = useGamesResultsResultIdUpdateMutation({
    resultId: resultId ?? 0,
  });

  const handleCommentSubmit = () => {
    if (!resultId || !comment.trim()) {
      return;
    }

    setIsSubmitting(true);
    updateComment(
      {
        comment: comment.trim(),
        imageUrl: imageUrl ?? '',
      },
      {
        onSuccess: () => {
          setIsSubmitting(false);
          setIsSubmitted(true);
          // stats 쿼리 invalidate하여 랭킹 보드 새로고침
          queryClient.invalidateQueries({
            queryKey: [API_ENDPOINT.CLIENT_V1_GAMES_STATS],
          });
          onCommentSubmit();
        },
        onError: () => {
          setIsSubmitting(false);
          alert('코멘트 저장에 실패했습니다. 다시 시도해주세요.');
        },
      },
    );
  };

  const handleClose = () => {
    setComment('');
    setIsSubmitted(false);
    onClose();
  };

  return (
    <Modal.Root
      opened={isOpen}
      onClose={handleClose}
      size={isSubmitted ? 'sm' : 'md'}
      closeOnClickOutside={false}
    >
      <Modal.Overlay />
      <Modal.Content>
        <Modal.Header>
          <Modal.Title>{isSubmitted ? '저장 완료!' : '프로필 설정 완료!'}</Modal.Title>
          <Modal.CloseButton />
        </Modal.Header>
        <Modal.Body>
          <Stack gap="md" align="center" py="md">
            {!isSubmitted && imageUrl && (
              <OptimizeImage
                src={imageUrl}
                alt="캐릭터 이미지"
                w={200 * 2}
                width={200}
                style={{
                  borderRadius: '12px',
                  border: '2px solid #e0e0e0',
                }}
              />
            )}
            {isSubmitted ? (
              <Stack align="center" gap="lg">
                <OptimizeImage
                  src={`${process.env.NEXT_PUBLIC_CDN_URL}/public/games/ch_s_santa.png`}
                  alt="캐릭터 이미지"
                  width={100}
                  w={100 * 2}
                />
                <Text size="md" c="gray.7" ta="center" fw={600}>
                  당신이 꾸며준 멋진 상배 요정이
                  <br />
                  피드에 올라갔어요! 메리 크리스마스 ♥
                </Text>
              </Stack>
            ) : (
              <Stack gap="xs" w="100%">
                <Text size="sm" c="gray.7" fw={500}>
                  소감 한마디를 남겨보세요
                </Text>
                <Textarea
                  placeholder="인프런 짱!"
                  value={comment}
                  onChange={(e) => setComment(e.target.value)}
                  minRows={3}
                  maxRows={6}
                  maxLength={200}
                />
                <Text size="xs" c="gray.5">
                  {comment.length} / 200자
                </Text>
                <Text size="xs" c="gray.6">
                  • 문구는 수정할 수 없으니 신중하게 작성해주세요.
                  <br />• 스팸, 욕설 등 부적절한 내용은 제재를 받을 수 있습니다.
                </Text>
              </Stack>
            )}
          </Stack>
        </Modal.Body>
        <Modal.Footer>
          {isSubmitted ? (
            <Button size="md" variant="filled" onClick={handleClose} fullWidth>
              고마워 ♥ 메리 크리스마스
            </Button>
          ) : (
            <Flex gap="sm" w="100%">
              <Button
                size="md"
                variant="default"
                onClick={handleClose}
                styles={{
                  root: { flexGrow: 1 },
                }}
              >
                닫기
              </Button>
              <Button
                size="md"
                variant="filled"
                onClick={handleCommentSubmit}
                styles={{
                  root: { flexGrow: 1 },
                }}
                disabled={!comment.trim() || isSubmitting || !resultId}
                loading={isSubmitting}
              >
                저장하기
              </Button>
            </Flex>
          )}
        </Modal.Footer>
      </Modal.Content>
    </Modal.Root>
  );
};
